import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";

/**
 * Injects Google Analytics 4 (gtag) and Meta Pixel scripts into <head> whenever those
 * integrations are enabled in Admin → Integrations. Also fires page_view / PageView
 * events on client-side route changes since GA4 doesn't auto-detect SPA navigations.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 * (unrelated to auth, but we do NOT hardcode the GA ID here — it's fetched from the backend
 * so admins can update it via CMS without a redeploy).
 */
export default function Analytics() {
  const location = useLocation();
  const gaIdRef = useRef(null);
  const pixelIdRef = useRef(null);
  const initedRef = useRef(false);

  // Fetch IDs once on mount + install scripts
  useEffect(() => {
    if (initedRef.current) return;
    initedRef.current = true;
    api.get("/public-integrations").then(({ data }) => {
      const gaId = (data?.google_analytics_id || "").trim();
      const pixelId = (data?.meta_pixel_id || "").trim();

      // ---------- Google Analytics 4 ----------
      if (gaId && /^G-[A-Z0-9]{6,12}$/.test(gaId) && !document.getElementById("ga4-loader")) {
        gaIdRef.current = gaId;
        const s1 = document.createElement("script");
        s1.id = "ga4-loader";
        s1.async = true;
        s1.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
        document.head.appendChild(s1);
        const s2 = document.createElement("script");
        s2.id = "ga4-init";
        s2.text = `
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          window.gtag = gtag;
          gtag('js', new Date());
          gtag('config', '${gaId}', { send_page_view: true });
        `;
        document.head.appendChild(s2);
      }

      // ---------- Meta / Facebook Pixel ----------
      if (pixelId && /^\d{6,20}$/.test(pixelId) && !document.getElementById("meta-pixel-init")) {
        pixelIdRef.current = pixelId;
        const s = document.createElement("script");
        s.id = "meta-pixel-init";
        s.text = `
          !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
          n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,
          document,'script','https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '${pixelId}');
          fbq('track', 'PageView');
        `;
        document.head.appendChild(s);
      }
    }).catch(() => {
      // Analytics is best-effort — never break the app if the config fetch fails.
    });
  }, []);

  // Fire page_view on every client-side route change (SPA)
  useEffect(() => {
    if (gaIdRef.current && typeof window.gtag === "function") {
      window.gtag("event", "page_view", {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
    if (pixelIdRef.current && typeof window.fbq === "function") {
      window.fbq("track", "PageView");
    }
  }, [location.pathname, location.search]);

  return null;
}
